<template>
  <SoButton
    title="Install Codes Upload"
    data-testid="install-codes-upload-btn"
    prepend-icon="upload"
    large
    @click="showModal = true"
  />
  <SoDialog v-model="showModal" card-title="Bulk upload install codes">
    <div>
      <p class="mb-4">
        Upload a CSV file to add or update install codes. Download the template
        below to see the expected format.
      </p>
    </div>
    <SoFileInputField
      v-model="file"
      class="mb-4"
      file-types="csv"
      label="Upload CSV"
      placeholder="Select a file"
    />
    <div v-if="!isLoading">
      <SoAlert
        v-if="alertState"
        :text="alertState.text"
        :type="alertState.type"
        data-testid="install-codes-alert"
      />
      <div class="d-flex justify-end my-3">
        <SoExportCsvButton
          v-if="hasDownloadErrors"
          :data="errorDownloadConfig.data"
          :labels="errorDownloadConfig.headers"
          :file-name="errorDownloadConfig.fileName"
          :async-download="false"
          button-text="Download Errors"
          prepend-icon="download"
          data-testid="install-codes-error-download"
        />
      </div>
    </div>
    <div class="d-flex justify-space-between mt-4">
      <SoExportCsvButton
        :data="templateDownloadConfig.data"
        :labels="templateDownloadConfig.headers"
        :file-name="templateDownloadConfig.fileName"
        :async-download="false"
        button-text="Download Template"
        prepend-icon="download"
        data-testid="install-codes-template-download"
      />
      <div class="d-flex gap-2">
        <SoButton title="Cancel" large @click="showModal = false" />
        <SoButton
          title="Upload"
          type="primary"
          large
          :disabled="file.length === 0"
          :loading="isLoading"
          data-testid="install-codes-submit"
          @click="uploadFile"
        />
      </div>
    </div>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoFileInputField from "@/components/formControls/SoFileInputField.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoAlert, { AlertTypes } from "@/components/SoAlert.vue"
  import SoExportCsvButton from "@/components/dataTable/SoExportCsvButton.vue"
  import { BulkInstallCodesUploadMutator } from "@/mutators/smartMeter/BulkInstallCodesUploadMutator"
  import type {
    InstallCodeRow,
    InstallCodeRowError,
    InstallCodesUploadResponse,
  } from "@/api/backofficeRest/generated/model"
  import { errorReporter } from "@/services/ErrorService"
  import { SoDataTableHeader } from "@/types/components/SoDataTable"
  import { formatDateDashed } from "@/helpers/dateHelper"
  import dayjs from "dayjs"
  import { formatError, parseFile, UploadResult } from "./fileUploaderHelper"

  const installCodeHeaders: SoDataTableHeader<keyof InstallCodeRow>[] = [
    { title: "install_code", key: "installCode" },
    { title: "device_id", key: "deviceId" },
    { title: "msn", key: "msn" },
    { title: "map_id", key: "mapId" },
    { title: "map_name", key: "mapName" },
    { title: "asn_date", key: "asnDate" },
    { title: "asn_outcome", key: "asnOutcome" },
    { title: "requested_date", key: "requestedDate" },
    { title: "last_date_checked", key: "lastDateChecked" },
    { title: "comment", key: "comment" },
  ]

  const installCodeErrorHeaders: SoDataTableHeader<
    keyof InstallCodeRowError
  >[] = [
    { title: "row_number", key: "rowNumber" },
    ...installCodeHeaders,
    { title: "error_reason", key: "errorReason" },
  ]

  export default defineComponent({
    components: {
      SoFileInputField,
      SoDialog,
      SoButton,
      SoAlert,
      SoExportCsvButton,
    },
    data() {
      return {
        showModal: false,
        file: [] as File[],
        isLoading: false,
        uploadResult: null as UploadResult<InstallCodeRowError> | null,
        hasNetworkError: false,
        uploadedFileName: "",
        uploadMutator: BulkInstallCodesUploadMutator.upload(),
      }
    },
    computed: {
      alertState(): { text: string; type: AlertTypes } | null {
        return formatError<InstallCodeRowError>(
          this.hasNetworkError,
          this.uploadResult,
        )
      },
      errorDownloadConfig() {
        const date = formatDateDashed(dayjs())

        return {
          data: { isLoading: false, items: this.uploadResult?.errors ?? [] },
          headers: installCodeErrorHeaders,
          fileName: `install-codes-errors-${date} - ${this.uploadedFileName}`,
        }
      },
      hasDownloadErrors() {
        return this.errorDownloadConfig.data.items.length > 0
      },
      templateDownloadConfig() {
        return {
          data: {
            isLoading: false,
            items: [
              {
                installCode: "AB12CD34",
                deviceId: "D1E2F3",
                msn: "A1B2C3D4",
                mapId: "MAP001",
                mapName: "North Region",
                asnDate: "2026-01-01",
                asnOutcome: "SUCCESS",
                requestedDate: "2025-12-20",
                lastDateChecked: "2026-01-02",
                comment: "Initial install",
              },
            ],
          },
          headers: installCodeHeaders,
          fileName: "install-codes-template.csv",
        }
      },
    },
    watch: {
      showModal(val: boolean) {
        if (!val) {
          this.file = []
          this.uploadResult = null
          this.hasNetworkError = false
          this.uploadedFileName = ""
        }
      },
    },
    methods: {
      async uploadFile() {
        if (this.file.length === 0) return

        this.isLoading = true
        this.uploadResult = null
        this.hasNetworkError = false

        try {
          const csvFile = this.file[0]
          this.uploadedFileName = csvFile.name
          const text = await csvFile.text()
          const parsed = parseFile<InstallCodeRow>(text, installCodeHeaders)

          if (parsed.error !== null) {
            this.uploadResult = {
              totalCount: 0,
              addedCount: 0,
              updatedCount: 0,
              errorCount: 0,
              errors: [],
              fileError: parsed.error,
            }
            return
          }

          const result = (await this.uploadMutator.mutateAsync({
            rows: parsed.rows,
          })) as InstallCodesUploadResponse

          this.uploadResult = {
            totalCount: result.totalCount ?? 0,
            addedCount: result.addedCount ?? 0,
            updatedCount: result.updatedCount ?? 0,
            errorCount: result.errorCount ?? 0,
            errors: result.errors ?? [],
            fileError: result.fileError,
          }
        } catch (error) {
          this.hasNetworkError = true
          errorReporter.report(error)
        } finally {
          this.isLoading = false
        }
      },
    },
  })
</script>
